import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, generatePath } from 'react-router';
import { RoutePaths } from '../Routes';
import { stackActions } from '../store/stack/actions';
import { useTranslation } from 'react-i18next';
import '../translations/translations';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from '@material-ui/core';

import SyncProblemIcon from '@material-ui/icons/SyncProblem';

import { ApplicationState } from '../store';
import { ListStacksResponseItem } from '../.generated/admin-api';
import { LocalStack } from '../store/stack/types';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: '4.5rem',
    width: '100%',
    height: '100%',
  },
  tableHeadCell: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    padding: '0.7rem 1rem 0.5rem 1rem',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  preDataView: {
    paddingTop: '14rem',
    textAlign: 'center',
  },
  tableBody: {
    overflowY: 'auto',
  },
  localStackCell: {
    position: 'relative',
    color: theme.palette.error.main,
    fontWeight: 'bold',
    // color: 'red',
  },
  localRefreshButton: {
    position: 'absolute',
    top: '0.13rem',
    color: theme.palette.error.main,
    right: 0,
    marginRight: theme.spacing(2),
  },
  localSyncSpinnerIcon: {
    position: 'absolute',
    top: '0.9rem',
    color: theme.palette.error.main,
    right: 0,
    marginRight: theme.spacing(3),
  },
}));

function formatDateString(dateString: string) {
  const date = new Date(dateString);
  return date ? date.toLocaleDateString(navigator.language) : '';
}

function StackTable(): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [localStackList, setLocalStackList] = useState<Array<LocalStack>>([]);

  const { t } = useTranslation(['translations']);
  const { departmentId, stackList, localStacks } = useSelector(
    (state: ApplicationState) => {
      if (!state.user.selectedDepartment) {
        return {};
      }

      const department =
        state.stacks.departments[state.user.selectedDepartment.id];
      if (department) {
        return {
          departmentId: state.user.selectedDepartment.id,
          stackList: department.stackList,
          localStacks: department.localStacks,
        };
      }
      return {};
    },
  );

  useEffect(() => {
    // convert localStacks to list
    if (localStacks) {
      // TODO: sort by date
      setLocalStackList(
        Object.keys(localStacks).map((key) => localStacks[key]),
      );
    } else {
      setLocalStackList([]);
    }
  }, [localStacks]);

  let preDataView = null;
  if (!stackList) {
    preDataView = (
      <Box className={classes.preDataView}>
        <CircularProgress color="secondary" />
      </Box>
    );
  } else if (stackList.length === 0) {
    preDataView = (
      <Box className={classes.preDataView}>
        <Typography variant="body1">{t('overview_page.no_stacks')}</Typography>
      </Box>
    );
  }

  async function handleUpload(localStackId: string) {
    if (departmentId) {
      await dispatch(stackActions.uploadStack(departmentId, localStackId));
      dispatch(stackActions.getStacksByDepartmentId(departmentId));
    }
  }

  function handleStackClicked(stack: ListStacksResponseItem) {
    history.push(generatePath(RoutePaths.Detail, { stackId: stack.stackId }));
  }

  function handleLocalStackClicked(localStack: LocalStack) {
    history.push(generatePath(RoutePaths.Detail, { stackId: localStack.id }));
  }

  return (
    <TableContainer className={classes.root}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>
              {t('overview_page.stack_no')}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>
              {t('overview_page.date')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {localStackList.map((localStack: LocalStack, i) => {
            const loading = localStack.uploadInProgress;
            return (
              <TableRow
                key={i}
                hover={true}
                onClick={() => {
                  handleLocalStackClicked(localStack);
                }}
              >
                <TableCell className={classes.localStackCell}>
                  {localStack.stackNumber}
                </TableCell>
                <TableCell className={classes.localStackCell}>
                  {formatDateString(localStack.date)}
                  {loading ? (
                    <CircularProgress
                      size="1.3rem"
                      className={classes.localSyncSpinnerIcon}
                    />
                  ) : (
                    <IconButton
                      className={classes.localRefreshButton}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleUpload(localStack.id);
                      }}
                    >
                      <SyncProblemIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {stackList?.map((stack: ListStacksResponseItem) => (
            <TableRow
              key={stack.stackId}
              hover={true}
              onClick={() => {
                handleStackClicked(stack);
              }}
            >
              <TableCell>{stack.stackNumber}</TableCell>
              <TableCell>
                {formatDateString(stack.lastModifiedDateTime)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {preDataView}
    </TableContainer>
  );
}

export { StackTable };
